import React, { RefObject, ReactElement, ReactNode, useState } from 'react';
import { createPortal } from 'react-dom';

import { ActionIcon, Tooltip } from '@any-ui-react/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDndMonitor,
  UniqueIdentifier,
} from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import {
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useHover, useMergedRef } from '@mantine/hooks';
import cx from 'classnames';
import { VariantProps, tv } from 'tailwind-variants';

import { ChevronRightIcon, TrashIcon } from '@anyx/shared-icons';
import { SlotsToClasses } from '@anyx/shared-ui';


import { DragHandler } from './DragHandler';

type ActiveOverlay = ({ itemId }: { itemId: UniqueIdentifier }) => React.ReactElement;

export interface SortableRootProps {
  itemsId: UniqueIdentifier[];
  className?: string;
  activeOverlay?: ActiveOverlay;
  onMove?: (from: number, to: number) => void;
  onActiveItemChange?: (itemId?: UniqueIdentifier) => void;
  children: NonNullable<ReactElement<SortableCardProps>>[];
}

const styles = tv({
  slots: {
    card: 'relative grid grid-cols-[1fr_auto] items-center gap-x-2',
    sortableElement:
      'text-gray-7 relative inline-flex h-12 w-full cursor-pointer items-center space-x-2 rounded-sm border px-2 text-sm font-semibold',
    dragHandler:
      'text-gray-7 hover:border-gray-5 active:bg-gray-0 hover:text-gray-9 active:text-gray-9 h-8 w-8 cursor-grab rounded-sm border border-transparent',
  },
  variants: {
    cursor: {
      grabbing: {
        card: '**:cursor-grabbing',
      },
      pointer: {
        sortableElement: 'cursor-pointer',
      },
      auto: {
        sortableElement: 'cursor-auto',
      },
    },
  },
});

type SortableCardVariants = VariantProps<typeof styles>;
type SortableCardSlots = keyof ReturnType<typeof styles>;

export interface SortableCardProps extends SortableCardVariants {
  id: string;
  error?: boolean;
  disabled?: boolean;
  disableRemove?: boolean;
  hideRemove?: boolean;
  render: string | React.ReactNode;
  deleteTooltip?: string;
  preventClick?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
  onMouseOver?: () => void;
  onMouseLeave?: () => void;
  classNames?: SlotsToClasses<SortableCardSlots>;
}

const OverlayCard = ({
  activeItemId,
  activeOverlay: ActiveOverlay,
  render,
}: {
  activeItemId: UniqueIdentifier;
  activeOverlay?: ActiveOverlay;
  render: ReactNode;
}) => {
  const { card } = styles({ cursor: 'grabbing' });

  if (ActiveOverlay) {
    return <ActiveOverlay itemId={activeItemId} />;
  }

  return (
    <SortableCard
      render={render}
      id={activeItemId.toString()}
      hideRemove
      classNames={{ card: card() }}
    />
  );
};

export function SortableRoot({
  itemsId,
  className,
  onMove,
  onActiveItemChange,
  children,
  activeOverlay,
}: SortableRootProps) {
  const [activeItemId, setActiveItemId] = useState<UniqueIdentifier>();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getIndex = (id: UniqueIdentifier) => itemsId.indexOf(id);
  const activeIndex = activeItemId ? getIndex(activeItemId) : -1;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={itemsId} strategy={verticalListSortingStrategy}>
        <ul className={className}>
          {React.Children.map(children, (child) => {
            const childClassNames = {
              ...child.props.classNames,
            };

            childClassNames.card = cx(childClassNames.card || '', {
              'opacity-30': child.props.id === activeItemId,
            });

            return {
              ...child,
              props: {
                ...child.props,
                classNames: childClassNames,
              },
            };
          })}
        </ul>
      </SortableContext>
      {createPortal(
        <DragOverlay>
          {activeItemId && (
            <OverlayCard
              render={children[activeIndex]?.props.render}
              activeOverlay={activeOverlay}
              activeItemId={activeItemId}
            />
          )}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );

  function handleDragStart(event: DragStartEvent) {
    const { active } = event;

    onActiveItemChange?.(active.id);
    setActiveItemId(active.id);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = itemsId.indexOf(active.id);
      const newIndex = itemsId.indexOf(over.id);

      onMove?.(oldIndex, newIndex);
    }
    onActiveItemChange?.(undefined);
    setActiveItemId(undefined);
  }
}

interface SortableElementProps {
  error?: boolean;
  disabled?: boolean;
  className?: string;
  id: string;
  onClick?: () => void;
  preventClick?: boolean;
  children: (props: {
    dragListeners?: SyntheticListenerMap;
    setDragNodeRef: RefObject<HTMLButtonElement>;
    itemHovered: boolean;
    dragHovered: boolean;
  }) => React.ReactElement;
}

export const SortableElement = React.forwardRef<HTMLDivElement, SortableElementProps>(
  ({ error, disabled, id, className, children, preventClick, ...props }, ref) => {
    const [isDragging, setIsDragging] = useState(false);
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    useDndMonitor({
      onDragStart() {
        setIsDragging(true);
      },
      onDragEnd() {
        setIsDragging(false);
      },
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const { hovered: itemHovered, ref: itemNodeRef } = useHover<HTMLDivElement>();
    const { hovered: dragHovered, ref: dragNodeRef } = useHover<HTMLButtonElement>();

    const mergedRef = useMergedRef(ref, itemNodeRef, setNodeRef);

    const classVariants = cx(
      {
        'border-failure bg-failure/[.08]': error,
        'active:bg-gray-0 active:border-gray-5': !error && !isDragging && !preventClick,
        'hover:bg-gray-0 hover-transition': !error && !dragHovered && !preventClick,
        'border-gray-2 bg-white': !error,
        'pl-4': disabled,
      },
      className
    );

    return (
      <div
        ref={mergedRef}
        style={style}
        className={`${classVariants} rounded-sm border`}
        {...attributes}
        {...props}
      >
        {children({
          dragListeners: listeners,
          setDragNodeRef: dragNodeRef as RefObject<HTMLButtonElement>,
          itemHovered,
          dragHovered,
        })}
      </div>
    );
  }
);

export const SortableCard = ({
  id,
  error,
  disabled,
  disableRemove,
  hideRemove,
  render,
  deleteTooltip,
  onClick,
  onRemove,
  onMouseOver,
  onMouseLeave,
  classNames,
  preventClick,
}: SortableCardProps) => {
  const { card, sortableElement, dragHandler } = styles({
    cursor: disabled || preventClick ? 'auto' : 'pointer',
  });

  return (
    <li
      className={card({ class: classNames?.card })}
      onMouseEnter={onMouseOver}
      onMouseLeave={onMouseLeave}
    >
      <SortableElement
        id={id}
        className={sortableElement({
          class: classNames?.sortableElement,
        })}
        error={error}
        disabled={disabled}
        onClick={onClick}
        preventClick={preventClick}
      >
        {({ dragListeners, setDragNodeRef, itemHovered, dragHovered }) => (
          <>
            {!disabled && (
              <DragHandler
                {...dragListeners}
                ref={setDragNodeRef}
                className={dragHandler({ class: classNames?.dragHandler })}
              />
            )}
            {render}
            {itemHovered && !dragHovered && !preventClick && (
              <ChevronRightIcon className="ml-auto! h-4 w-4 stroke-2" />
            )}
          </>
        )}
      </SortableElement>
      {!hideRemove && (
        <Tooltip label={deleteTooltip}>
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={onRemove}
            disabled={disabled || disableRemove}
          >
            <TrashIcon className="h-4 w-4" />
          </ActionIcon>
        </Tooltip>
      )}
    </li>
  );
};
